import type { PropsWithChildren } from "react";

import {
  Avatar,
  AvatarFallback,
  Badge,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@alaffia-technology-solutions/ds";
import { cn } from "@alaffia-technology-solutions/tailwind-utils";
import {
  formatDate,
  getUserInitials,
} from "@alaffia-technology-solutions/utils";

import type { PDFAnnotation } from "./types";

interface PDFAnnotationsItemProps extends PropsWithChildren {
  annotation: PDFAnnotation;
  active?: boolean;
}

export function PDFAnnotationsItem({
  annotation,
  active,
  children,
}: PDFAnnotationsItemProps) {
  return (
    <li
      className="af-group af-relative"
      ref={(el) => {
        if (active && el) {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }}
    >
      <Tooltip>
        <TooltipTrigger
          className={cn(
            "af-flex af-w-full af-flex-col af-items-start af-gap-2 af-rounded af-border af-border-transparent af-p-2 af-text-xs hover:af-bg-primary-foreground",
            {
              "af-bg-interface-accent": active,
            },
          )}
        >
          <div className="af-flex af-w-full af-items-center af-gap-2 af-overflow-hidden">
            <Avatar className="af-h-7 af-w-7">
              <AvatarFallback className="af-text-xs">
                {getUserInitials(
                  annotation.creator.firstName,
                  annotation.creator.lastName,
                )}
              </AvatarFallback>
            </Avatar>
            <span className="af-truncate">
              {annotation.creator.firstName} {annotation.creator.lastName}
            </span>
            <span className="af-text-muted-foreground">
              {formatDate({ date: annotation.createdAt })}
            </span>
          </div>
          <span className="af-line-clamp-2 af-text-start">
            {removeWhitespace(annotation.sourceText ?? "")}
          </span>
        </TooltipTrigger>
        <PDFAnnotationsItemTooltipContent annotation={annotation} />
      </Tooltip>
      {children}
    </li>
  );
}

interface PDFAnnotationsItemTooltipContentProps {
  annotation: PDFAnnotation;
}

function PDFAnnotationsItemTooltipContent({
  annotation,
}: PDFAnnotationsItemTooltipContentProps) {
  const { labels, comment } = annotation;
  return (
    <TooltipContent
      side="right"
      className="af-max-h-60 af-max-w-60 af-overflow-y-auto"
    >
      <div className="af-mb-2">
        <TooltipContentSubtitle>Labels</TooltipContentSubtitle>{" "}
        <div className="af-flex af-flex-wrap af-gap-1">
          {labels.length > 0 ? (
            labels.map((label) => (
              <Badge key={label} variant="secondary">
                {label}
              </Badge>
            ))
          ) : (
            <NoContent />
          )}
        </div>
      </div>
      <div>
        <TooltipContentSubtitle>Comment</TooltipContentSubtitle>{" "}
        {comment ? (
          <p className="af-break-words af-text-sm af-text-foreground">
            {comment}
          </p>
        ) : (
          <NoContent />
        )}
      </div>
    </TooltipContent>
  );
}

function TooltipContentSubtitle({ children }: PropsWithChildren) {
  return (
    <span className="af-mb-1 af-text-xs af-font-semibold af-text-muted-foreground">
      {children}
    </span>
  );
}

function NoContent() {
  return (
    <span className="af-block af-italic af-text-muted-foreground">None</span>
  );
}

function removeWhitespace(value: string) {
  return value.replace(/\s+/g, " ").trim();
}
